import { useTranslation } from "react-i18next";
import { IconMaterial } from "../iconMaterial/IconMaterial";

export enum StatusEnum {
  NEW = "new",
  DONE = "done",
  CANCELED = "canceled",
  REJECTED = "rejected",
  SENDING_TO_DEVICE = "sending_to_device",
  PENDING_ON_DEVICE = "pending_on_device",
  ERROR = "error",
  MAINTENANCE = "maintenance",
  ACTIVE = "active",
  INACTIVE = "inactive",
  ONLINE = "online",
  COMPLETED = "completed",
  ARCHIVED = "archived",
  IN_PROGRESS = "in_progress",
  NOT_PROVIDED = "not_provided",
}

interface BadgeProps {
  status: StatusEnum | string | boolean;
  className?: string;
  onClick?: any;
  leftIcon?: string
}

const statusConfig: Record<StatusEnum, { key: string; classes: string }> = {
  [StatusEnum.NEW]: {
    key: "new",
    classes: "bg-background-light-gray border border-secondary-stroke text-primary-dark",
  },
  [StatusEnum.DONE]: {
    key: "done",
    classes: "bg-success-light text-success-dark border border-success-stroke",
  },
  [StatusEnum.CANCELED]: {
    key: "canceled",
    classes: "bg-disabled text-primary-light border border-secondary-stroke",
  },
  [StatusEnum.REJECTED]: {
    key: "rejected",
    classes: "bg-error-light text-error-dark border border-error-dark",
  },
  [StatusEnum.SENDING_TO_DEVICE]: {
    key: "sending_to_device",
    classes: "bg-lightblue text-primeblue border border-primary-stroke",
  },
  [StatusEnum.PENDING_ON_DEVICE]: {
    key: "pending_on_device",
    classes: "bg-purpure-light text-purpure-dark border border-purpure-stroke",
  },
  [StatusEnum.ERROR]: {
    key: "error",
    classes: "bg-warning-light text-warning-dark border border-warning-stroke",
  },
  [StatusEnum.MAINTENANCE]: {
    key: "maintenance",
    classes: "bg-orange-100 text-orange-800 border border-warning-stroke",
  },
  [StatusEnum.ACTIVE]: {
    key: "active",
    classes: "bg-lightblue text-primeblue border border-primary-stroke",
  },
  [StatusEnum.INACTIVE]: {
    key: "inactive",
    classes: "bg-background-light-gray text-dark border border-secondary-stroke",
  },
  [StatusEnum.ONLINE]: {
    key: "online",
    classes: "bg-lightblue text-primeblue border border-primary-stroke",
  },
  [StatusEnum.COMPLETED]: {
    key: "completed",
    classes: "bg-background-light-gray text-dark border border-secondary-stroke",
  },
  [StatusEnum.ARCHIVED]: {
    key: "archived",
    classes: "bg-background-light-gray text-dark border border-secondary-stroke",
  },
  [StatusEnum.IN_PROGRESS]: {
    key: "in_progress",
    classes: "bg-lightblue text-primeblue border border-primary-stroke",
  },
  [StatusEnum.NOT_PROVIDED]: {
    key: "not_provided",
    classes: "bg-warning-light text-warning-dark border border-warning-stroke",
  },
};

export default function Badge({ status, className, onClick, leftIcon }: BadgeProps) {
  const { t } = useTranslation();

  let normalizedStatus: StatusEnum = StatusEnum.NOT_PROVIDED;

  if (typeof status === "boolean") {
    normalizedStatus = status ? StatusEnum.ACTIVE : StatusEnum.INACTIVE;
  } else {
    const lower = String(status).toLowerCase();
    normalizedStatus =
      (Object.values(StatusEnum).find((s) => s === lower) as StatusEnum) ??
      StatusEnum.NOT_PROVIDED;
  }

  const { key, classes } = statusConfig[normalizedStatus];
  const translatedText = t(`statuses.${key}`, key.replace(/_/g, " "));

  return (
    <span
      className={`flex items-center gap-1.5 inline-flex px-3 py-1 text-xs font-medium rounded-full group ${classes} ${className} ${onClick && 'hover:bg-white'}`}
      onClick={() => onClick && onClick()}
    >
      {translatedText}
      {leftIcon && (
        <IconMaterial
          filled
          icon={leftIcon}
          className={`${classes} border-none ${onClick && 'group-hover:bg-white'}`}
          size={14} />
      )}
    </span>
  );
}
